import { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Search, ChevronDown, ChevronRight, Plus, Trash2, Check, X,
  Sparkles, Loader2, BookOpen, Copy, Download, FileText,
  MessageSquare, Briefcase, Target, StickyNote, Calendar, Edit2,
  LayoutGrid, List, Archive, ArchiveRestore
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '';

const ROUND_TYPES = [
  { value: 'phone_screen', label: 'Phone Screen' },
  { value: 'recruiter', label: 'Recruiter' },
  { value: 'technical', label: 'Technical' },
  { value: 'coding', label: 'Coding' },
  { value: 'onsite', label: 'Onsite' },
  { value: 'behavioral', label: 'Behavioral' },
  { value: 'hiring_manager', label: 'Hiring Manager' },
  { value: 'final', label: 'Final' },
  { value: 'other', label: 'Other' },
];

const ROUND_STATUSES = [
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const createEmptyRound = () => ({
  roundType: 'phone_screen',
  scheduledDate: '',
  interviewers: '',
  notes: '',
  status: 'scheduled'
});

const normalizeRound = (round = {}) => ({
  id: round.id,
  roundType: round.roundType || round.round_type || 'phone_screen',
  scheduledDate: round.scheduledDate || round.scheduled_date || '',
  interviewers: round.interviewers || '',
  notes: round.notes || '',
  status: round.status || 'scheduled'
});

const toRoundApiPayload = (round = {}) => ({
  roundType: round.roundType || 'phone_screen',
  scheduledDate: round.scheduledDate || null,
  interviewers: round.interviewers || '',
  notes: round.notes || '',
  status: round.status || 'scheduled'
});

// ============================================================================
// ATS Score Badge
// ============================================================================
function ATSScoreBadge({ score }) {
  const [showTooltip, setShowTooltip] = useState(false);

  if (!score) return null;

  const getScoreColor = (score) => {
    if (score >= 85) return 'text-green-600';
    if (score >= 70) return 'text-slate-600';
    return 'text-slate-400';
  };

  return (
    <div className="relative inline-block">
      <button
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        className={`text-xs font-medium ${getScoreColor(score)} cursor-help`}
      >
        {score}%
      </button>

      {showTooltip && (
        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-slate-900 text-white rounded-lg shadow-lg text-xs">
          <p className="text-white/70">Resume match score</p>
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-900"></div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Status Filter Pills - Visual colored chips on all screen sizes
// ============================================================================
function StatusFilters({ counts, active, onChange }) {
  const statuses = [
    { id: 'all', label: 'All', color: null, bgActive: 'bg-slate-900', textActive: 'text-white' },
    { id: 'draft', label: 'Drafts', color: 'bg-amber-400', bgActive: 'bg-amber-50', textActive: 'text-amber-700', border: 'border-amber-200' },
    { id: 'applied', label: 'Applied', color: 'bg-cyan-500', bgActive: 'bg-cyan-50', textActive: 'text-cyan-700', border: 'border-cyan-200' },
    { id: 'interview', label: 'Interview', color: 'bg-primary', bgActive: 'bg-primary/10', textActive: 'text-primary', border: 'border-primary/20' },
    { id: 'offer', label: 'Offers', color: 'bg-green-500', bgActive: 'bg-green-50', textActive: 'text-green-700', border: 'border-green-200' },
    { id: 'rejected', label: 'Rejected', color: 'bg-slate-400', bgActive: 'bg-slate-100', textActive: 'text-slate-600', border: 'border-slate-200' },
    { id: 'archived', label: 'Archived', color: 'bg-slate-300', bgActive: 'bg-slate-100', textActive: 'text-slate-500', border: 'border-slate-200' },
  ];

  return (
    <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
      {statuses.map((s) => {
        const count = counts[s.id] || 0;
        const isActive = active === s.id;

        return (
          <button
            key={s.id}
            onClick={() => onChange(s.id)}
            className={`
              flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-all cursor-pointer flex-shrink-0
              ${isActive
                ? `${s.bgActive} ${s.textActive} ${s.border ? `border ${s.border}` : ''} shadow-sm`
                : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-300 hover:bg-slate-50'
              }
            `}
          >
            {s.color && (
              <span className={`w-2 h-2 rounded-full ${s.color} ${!isActive ? 'opacity-60' : ''}`}></span>
            )}
            <span className="hidden sm:inline">{s.label}</span>
            <span className="sm:hidden">{s.label.slice(0, 3)}</span>
            <span className={`text-xs ${isActive ? 'opacity-80' : 'text-slate-400'}`}>
              {count}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ============================================================================
// Application Row - Minimal list item
// ============================================================================
function ApplicationRow({ app, onView, onStatusChange, onArchive }) {
  const statusDot = {
    draft: 'bg-amber-400',
    applied: 'bg-secondary',
    interview: 'bg-primary',
    interviewing: 'bg-primary',
    offer: 'bg-green-500',
    rejected: 'bg-slate-400',
  };
  const notesPreview = app.notes?.trim();
  const subtitle = app.role || (notesPreview ? notesPreview : 'Role not specified');

  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div
      onClick={() => onView(app)}
      className={`group flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer overflow-hidden ${
        app.archived ? 'opacity-50' : ''
      }`}
    >
      {/* Status dot */}
      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${statusDot[app.status] || statusDot.draft}`} />

      {/* Content */}
      <div className="flex-1 min-w-0 max-w-[60%] sm:max-w-none">
        <p className="text-sm font-medium text-slate-900 truncate max-w-full">
          {app.company || 'Unknown Company'}
        </p>
        <p className="text-xs text-slate-500 truncate max-w-full">
          {subtitle}
        </p>
      </div>

      {/* ATS Score */}
      {app.qualityScore && (
        <span className="hidden sm:block">
          <ATSScoreBadge score={app.qualityScore} />
        </span>
      )}

      {/* Status dropdown - desktop */}
      <div className="hidden sm:flex items-center gap-2">
        {!app.archived && (
          <select
            value={app.status}
            onChange={(e) => {
              e.stopPropagation();
              onStatusChange(app.id, e.target.value, e.target.value === 'applied' ? new Date().toISOString() : null);
            }}
            onClick={(e) => e.stopPropagation()}
            className="appearance-none text-xs text-slate-500 bg-transparent cursor-pointer focus:outline-none pr-4 capitalize"
            style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0 center' }}
          >
            <option value="draft">Draft</option>
            <option value="applied">Applied</option>
            <option value="interview">Interview</option>
            <option value="offer">Offer</option>
            <option value="rejected">Rejected</option>
          </select>
        )}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onArchive(app.id, !app.archived);
          }}
          className="p-1 rounded text-slate-400 hover:text-slate-600 opacity-0 group-hover:opacity-100 transition-all cursor-pointer"
        >
          {app.archived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
        </button>
      </div>

      {/* Date */}
      <span className="text-xs text-slate-400 flex-shrink-0">{formatDate(app.timestamp)}</span>

      <ChevronRight size={14} className="text-slate-300 flex-shrink-0" />
    </div>
  );
}

// ============================================================================
// Application Card - Minimal card
// ============================================================================
function ApplicationCard({ app, onView, onArchive }) {
  const statusDot = {
    draft: 'bg-amber-400',
    applied: 'bg-secondary',
    interview: 'bg-primary',
    interviewing: 'bg-primary',
    offer: 'bg-green-500',
    rejected: 'bg-slate-400',
  };
  const notesPreview = app.notes?.trim();
  const subtitle = app.role || (notesPreview ? notesPreview : 'Role not specified');

  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div
      onClick={() => onView(app)}
      className={`group bg-white border border-slate-200 rounded-xl p-4 hover:border-slate-300 transition-colors cursor-pointer overflow-hidden ${
        app.archived ? 'opacity-50' : ''
      }`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${statusDot[app.status] || statusDot.draft}`} />
          <span className="text-xs text-slate-500 capitalize">{app.status}</span>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onArchive(app.id, !app.archived);
          }}
          className="p-1 rounded text-slate-400 hover:text-slate-600 opacity-0 group-hover:opacity-100 transition-all cursor-pointer flex-shrink-0"
        >
          {app.archived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
        </button>
      </div>

      {/* Content */}
      <h3 className="font-medium text-slate-900 text-sm truncate mb-1 max-w-full">
        {app.company || 'Unknown Company'}
      </h3>
      <p className="text-xs text-slate-500 truncate mb-3 max-w-full">
        {subtitle}
      </p>

      {/* Footer */}
      <div className="flex items-center justify-between text-xs text-slate-400">
        <span>{formatDate(app.timestamp)}</span>
        {app.qualityScore && <ATSScoreBadge score={app.qualityScore} />}
      </div>
    </div>
  );
}

// ============================================================================
// Application Detail Panel
// ============================================================================
function ApplicationDetailPanel({
  app,
  isOpen,
  onClose,
  onDownloadDocx,
  authToken,
  onUpdateApplication,
  onStatusChange,
  onArchive,
  onStartNewApplication
}) {
  const [activeSection, setActiveSection] = useState('resume');
  const [copied, setCopied] = useState(false);
  const [showChanges, setShowChanges] = useState(false);

  // Questions state
  const [questions, setQuestions] = useState([{ question: '', response: '', charLimit: 500, expanded: false }]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatingIndex, setGeneratingIndex] = useState(null);

  // Interview Prep state
  const [prepGuide, setPrepGuide] = useState(null);
  const [loadingPrep, setLoadingPrep] = useState(false);

  // Notes state
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  // Interview Rounds state
  const [interviewRounds, setInterviewRounds] = useState([]);
  const [loadingRounds, setLoadingRounds] = useState(false);
  const [editingRound, setEditingRound] = useState(null);
  const [newRound, setNewRound] = useState(null);

  // Edit company/role state
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [editedCompany, setEditedCompany] = useState('');
  const [editedRole, setEditedRole] = useState('');
  const [editedSalaryRangeMin, setEditedSalaryRangeMin] = useState('');
  const [editedSalaryRangeMax, setEditedSalaryRangeMax] = useState('');
  const [editedLocation, setEditedLocation] = useState('');
  const [isEditingJobDesc, setIsEditingJobDesc] = useState(false);
  const [editedJobDescription, setEditedJobDescription] = useState('');
  const [savingJobDesc, setSavingJobDesc] = useState(false);

  // Fetch interview rounds
  const fetchInterviewRounds = async () => {
    if (!app?.id) return;

    if (!authToken) {
      setInterviewRounds((app.interviewRounds || []).map(normalizeRound));
      return;
    }

    setLoadingRounds(true);
    try {
      const response = await fetch(`${API_URL}/api/user/applications/${app.id}/interviews`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setInterviewRounds((data.interviews || []).map(normalizeRound));
      }
    } catch (error) {
      console.error('Error fetching interview rounds:', error);
    } finally {
      setLoadingRounds(false);
    }
  };

  const persistLocalRounds = (nextRounds) => {
    setInterviewRounds(nextRounds);
    if (onUpdateApplication) {
      onUpdateApplication(app.id, { interviewRounds: nextRounds });
    }
  };

  // Add interview round
  const addInterviewRound = async (roundData) => {
    if (!app?.id) return;

    const payload = normalizeRound(roundData);

    if (!authToken) {
      const localRound = {
        ...payload,
        id: `local-round-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
      };
      const nextRounds = [...interviewRounds, localRound];
      persistLocalRounds(nextRounds);
      if (['draft', 'applied'].includes(app.status)) {
        onStatusChange(app.id, 'interview');
      }
      setNewRound(null);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/user/applications/${app.id}/interviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
        body: JSON.stringify(toRoundApiPayload(payload))
      });
      if (response.ok) {
        const data = await response.json();
        setInterviewRounds([...interviewRounds, normalizeRound(data.interview)]);
        setNewRound(null);
      }
    } catch (error) {
      console.error('Error adding interview round:', error);
    }
  };

  // Update interview round
  const updateInterviewRound = async (roundId, roundData) => {
    if (!app?.id) return;

    const payload = normalizeRound(roundData);

    if (!authToken) {
      const nextRounds = interviewRounds.map((round) =>
        round.id === roundId ? { ...payload, id: roundId } : round
      );
      persistLocalRounds(nextRounds);
      setEditingRound(null);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/user/applications/${app.id}/interviews/${roundId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
        body: JSON.stringify(toRoundApiPayload(payload))
      });
      if (response.ok) {
        const data = await response.json();
        setInterviewRounds(interviewRounds.map(r => r.id === roundId ? normalizeRound(data.interview) : r));
        setEditingRound(null);
      }
    } catch (error) {
      console.error('Error updating interview round:', error);
    }
  };

  // Delete interview round
  const deleteInterviewRound = async (roundId) => {
    if (!app?.id) return;

    if (!authToken) {
      const nextRounds = interviewRounds.filter(round => round.id !== roundId);
      persistLocalRounds(nextRounds);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/user/applications/${app.id}/interviews/${roundId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (response.ok) {
        setInterviewRounds(interviewRounds.filter(r => r.id !== roundId));
      }
    } catch (error) {
      console.error('Error deleting interview round:', error);
    }
  };

  useEffect(() => {
    if (app) {
      setPrepGuide(app.interviewPrep || null);
      setNotes(app.notes || '');
      setQuestions([{ question: '', response: '', charLimit: 500, expanded: false }]);
      const hasResumeContent = typeof app.resume === 'string' && app.resume.trim().length > 0;
      setActiveSection(hasResumeContent ? 'resume' : 'notes');
      setShowChanges(false);
      setInterviewRounds((app.interviewRounds || []).map(normalizeRound));
      setEditingRound(null);
      setNewRound(null);
      setIsEditingDetails(false);
      setEditedCompany(app.company || '');
      setEditedRole(app.role || '');
      setEditedSalaryRangeMin(app.salaryRangeMin ?? '');
      setEditedSalaryRangeMax(app.salaryRangeMax ?? '');
      setEditedLocation(app.location || '');
      setIsEditingJobDesc(false);
      fetchInterviewRounds();
    }
  }, [app?.id]);

  // Save edited company/role
  const saveCompanyRole = async () => {
    if (!app?.id) return;

    if (!authToken) {
      if (onUpdateApplication) {
        onUpdateApplication(app.id, { company: editedCompany, role: editedRole, salaryRangeMin: editedSalaryRangeMin || null, salaryRangeMax: editedSalaryRangeMax || null, location: editedLocation || null });
      }
      setIsEditingDetails(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/user/applications/${app.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
        body: JSON.stringify({ company: editedCompany, role: editedRole, salaryRangeMin: editedSalaryRangeMin || null, salaryRangeMax: editedSalaryRangeMax || null, location: editedLocation || null })
      });
      if (response.ok && onUpdateApplication) {
        onUpdateApplication(app.id, { company: editedCompany, role: editedRole, salaryRangeMin: editedSalaryRangeMin || null, salaryRangeMax: editedSalaryRangeMax || null, location: editedLocation || null });
      }
      setIsEditingDetails(false);
    } catch (error) {
      console.error('Error updating company/role:', error);
    }
  };

  const saveJobDescription = async () => {
    if (!app?.id) return;
    setSavingJobDesc(true);
    try {
      const response = await fetch(`${API_URL}/api/user/applications/${app.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
        body: JSON.stringify({ jobDescription: editedJobDescription })
      });
      if (response.ok && onUpdateApplication) {
        onUpdateApplication(app.id, { jobDescription: editedJobDescription });
      }
      setIsEditingJobDesc(false);
    } catch (error) {
      console.error('Error updating job description:', error);
    } finally {
      setSavingJobDesc(false);
    }
  };

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!app) return null;

  const handleCopy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleDownloadTxt = () => {
    const blob = new Blob([app.resume || ''], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${app.company || 'resume'}_tailored.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const generateResponse = async (index) => {
    const q = questions[index];
    if (!q.question.trim()) return;
    setIsGenerating(true);
    setGeneratingIndex(index);
    try {
      const response = await fetch(`${API_URL}/api/generate-app-response`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
        body: JSON.stringify({ question: q.question, charLimit: q.charLimit, jobDescription: app.jobDescription || '', tailoredResume: app.resume || '' })
      });
      if (!response.ok) throw new Error('Failed');
      const data = await response.json();
      const updated = [...questions];
      updated[index] = { ...updated[index], response: data.response };
      setQuestions(updated);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsGenerating(false);
      setGeneratingIndex(null);
    }
  };

  const updateQuestion = (index, field, value) => {
    const updated = [...questions];
    updated[index] = { ...updated[index], [field]: value };
    setQuestions(updated);
  };

  const getInterviewPrep = async () => {
    if (prepGuide) return;
    setLoadingPrep(true);
    try {
      const response = await fetch(`${API_URL}/api/user/applications/${app.id}/interview-prep`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        setPrepGuide(data.prep);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoadingPrep(false);
    }
  };

  const saveNotes = async () => {
    if (!app?.id) return;

    if (!authToken) {
      if (onUpdateApplication) onUpdateApplication(app.id, { notes });
      return;
    }

    setSavingNotes(true);
    try {
      await fetch(`${API_URL}/api/user/applications/${app.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
        body: JSON.stringify({ notes })
      });
      if (onUpdateApplication) onUpdateApplication(app.id, { notes });
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setSavingNotes(false);
    }
  };

  const navItems = [
    { id: 'resume', icon: FileText, label: 'Resume' },
    { id: 'jobdesc', icon: Briefcase, label: 'Job' },
    { id: 'questions', icon: MessageSquare, label: 'Q&A' },
    { id: 'rounds', icon: Calendar, label: 'Rounds' },
    { id: 'notes', icon: StickyNote, label: 'Notes' },
    { id: 'prep', icon: Target, label: 'Prep' },
  ];

  // Auto-derive round status from date
  const deriveRoundStatus = (dateStr) => {
    if (!dateStr) return 'scheduled';
    return new Date(dateStr) < new Date() ? 'completed' : 'scheduled';
  };

  // Interview Round Form
  const RoundForm = ({ round, onSave, onCancel }) => {
    const [formData, setFormData] = useState(normalizeRound(round || createEmptyRound()));

    const handleDateChange = (dateStr) => {
      setFormData({ ...formData, scheduledDate: dateStr, status: deriveRoundStatus(dateStr) });
    };

    return (
      <div className="bg-slate-50 rounded-lg p-4 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Type</label>
            <select
              value={formData.roundType}
              onChange={(e) => setFormData({ ...formData, roundType: e.target.value })}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-400 cursor-pointer"
            >
              {ROUND_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Date</label>
            <input
              type="date"
              value={formData.scheduledDate ? formData.scheduledDate.slice(0, 10) : ''}
              onChange={(e) => handleDateChange(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-400"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Interviewers</label>
          <input
            type="text"
            value={formData.interviewers}
            onChange={(e) => setFormData({ ...formData, interviewers: e.target.value })}
            placeholder="Names..."
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-400"
          />
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Notes</label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            rows={4}
            placeholder="How did it go? Key topics, impressions..."
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-y focus:outline-none focus:border-slate-400"
          />
        </div>
        <div className="flex items-center justify-end gap-2 pt-1">
          <button onClick={onCancel} className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg cursor-pointer">Cancel</button>
          <button onClick={() => onSave(formData)} className="px-3 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 cursor-pointer">Save</button>
        </div>
      </div>
    );
  };

  return createPortal(
    <div className={`fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 ${isOpen ? '' : 'pointer-events-none'}`}>
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/50 transition-opacity ${isOpen ? 'opacity-100' : 'opacity-0'}`}
        onClick={onClose}
      />

      {/* Panel - full height on mobile, centered modal on desktop */}
      <div className={`relative bg-white sm:rounded-xl shadow-lg w-full max-w-4xl h-[100dvh] sm:h-[85vh] sm:max-h-[85vh] flex flex-col sm:flex-row overflow-hidden transition-all ${isOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>

        {/* Mobile Header */}
        <div className="sm:hidden border-b border-slate-200">
          <div className="flex items-center justify-between p-4">
            <div className="min-w-0 flex-1">
              <h2 className="font-medium text-slate-900 truncate">{app.company || 'Company'}</h2>
              <p className="text-xs text-slate-500 truncate">{app.role || 'Role'}</p>
            </div>
            {/* Mobile journey progress - compact */}
            <div className="flex items-center gap-1 mr-3">
              <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                <Check size={10} className="text-white" />
              </div>
              <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                ['applied', 'interview', 'interviewing', 'offer'].includes(app.status) ? 'bg-green-500' : 'bg-slate-200'
              }`}>
                {['applied', 'interview', 'interviewing', 'offer'].includes(app.status)
                  ? <Check size={10} className="text-white" />
                  : <span className="text-[8px] font-medium text-slate-500">2</span>
                }
              </div>
              <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                app.interviewPrep ? 'bg-green-500' : 'bg-slate-200'
              }`}>
                {app.interviewPrep
                  ? <Check size={10} className="text-white" />
                  : <span className="text-[8px] font-medium text-slate-500">3</span>
                }
              </div>
            </div>
            <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 cursor-pointer">
              <X size={20} />
            </button>
          </div>
          {/* Mobile contextual prompt */}
          {app.status === 'draft' && (
            <div className="px-4 pb-2">
              <button
                onClick={() => onStatusChange(app.id, 'applied', new Date().toISOString())}
                className="w-full py-2 bg-primary/5 text-primary text-sm font-medium rounded-lg cursor-pointer"
              >
                Ready to apply? Mark as applied →
              </button>
            </div>
          )}
          {app.status === 'applied' && (
            <div className="px-4 pb-2 space-y-1.5">
              <button
                onClick={() => onStatusChange(app.id, 'interview')}
                className="w-full py-2 bg-primary/5 text-primary text-sm font-medium rounded-lg cursor-pointer"
              >
                Got an interview? Update status →
              </button>
              <button
                onClick={() => setActiveSection('notes')}
                className="w-full py-2 bg-slate-50 text-slate-600 text-sm font-medium rounded-lg cursor-pointer"
              >
                Add notes →
              </button>
            </div>
          )}
          {['interview', 'interviewing'].includes(app.status) && (
            <div className="px-4 pb-2 space-y-1.5">
              {!app.interviewPrep && (
                <button
                  onClick={() => setActiveSection('prep')}
                  className="w-full py-2 bg-primary/5 text-primary text-sm font-medium rounded-lg cursor-pointer"
                >
                  Generate interview prep →
                </button>
              )}
              <button
                onClick={() => setActiveSection('notes')}
                className="w-full py-2 bg-slate-50 text-slate-600 text-sm font-medium rounded-lg cursor-pointer"
              >
                Add notes about this interview →
              </button>
            </div>
          )}
          {app.status === 'offer' && (
            <div className="px-4 pb-2 space-y-1.5">
              <button
                onClick={() => setActiveSection('notes')}
                className="w-full py-2 bg-green-50 text-green-700 text-sm font-medium rounded-lg cursor-pointer"
              >
                Log offer details (salary, terms) →
              </button>
            </div>
          )}
          {app.status === 'rejected' && (
            <div className="px-4 pb-2 space-y-1.5">
              {(app.statusHistory || []).some(h => ['interview', 'interviewing', 'offer'].includes(h.toStatus)) && (
                <button
                  onClick={() => setActiveSection('notes')}
                  className="w-full py-2 bg-slate-50 text-slate-600 text-sm font-medium rounded-lg cursor-pointer"
                >
                  Add notes on what happened →
                </button>
              )}
              <button
                onClick={onStartNewApplication}
                className="w-full py-2 bg-primary/5 text-primary text-sm font-medium rounded-lg cursor-pointer"
              >
                Keep going — tailor another →
              </button>
            </div>
          )}
          <div className="flex overflow-x-auto px-4 pb-2 gap-1 scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveSection(item.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm whitespace-nowrap cursor-pointer flex-shrink-0 ${
                  activeSection === item.id
                    ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                <item.icon size={14} />
                {item.label}
              </button>
            ))}
          </div>
        </div>

        {/* Desktop Sidebar */}
        <div className="hidden sm:flex w-48 bg-slate-50 border-r border-slate-200 flex-col">
          <div className="p-4 border-b border-slate-200">
            {isEditingDetails ? (
              <div className="space-y-2">
                <input
                  type="text"
                  value={editedCompany}
                  onChange={(e) => setEditedCompany(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-slate-400"
                  autoFocus
                />
                <input
                  type="text"
                  value={editedRole}
                  onChange={(e) => setEditedRole(e.target.value)}
                  className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-slate-400"
                />
                <input
                  type="text"
                  value={editedLocation}
                  onChange={(e) => setEditedLocation(e.target.value)}
                  placeholder="Location (e.g. Remote, NYC)"
                  className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-slate-400"
                />
                <div className="flex gap-1.5 items-center">
                  <input
                    type="number"
                    value={editedSalaryRangeMin}
                    onChange={(e) => setEditedSalaryRangeMin(e.target.value)}
                    placeholder="Min $"
                    className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-slate-400"
                  />
                  <span className="text-xs text-slate-400 flex-shrink-0">–</span>
                  <input
                    type="number"
                    value={editedSalaryRangeMax}
                    onChange={(e) => setEditedSalaryRangeMax(e.target.value)}
                    placeholder="Max $"
                    className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-slate-400"
                  />
                </div>
                <div className="flex gap-1.5">
                  <button onClick={saveCompanyRole} className="flex-1 px-2 py-1 text-xs bg-primary text-white rounded-lg cursor-pointer hover:bg-primary/90">Save</button>
                  <button onClick={() => setIsEditingDetails(false)} className="px-2 py-1 text-xs text-slate-600 bg-slate-200 rounded-lg cursor-pointer">Cancel</button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-start justify-between mb-2">
                  <h2 className="font-medium text-slate-900 truncate">{app.company || 'Company'}</h2>
                  <button onClick={() => setIsEditingDetails(true)} className="p-1 text-slate-400 hover:text-slate-600 cursor-pointer">
                    <Edit2 size={12} />
                  </button>
                </div>
                <p className="text-xs text-slate-500 truncate">{app.role || 'Role'}</p>
                {app.location && (
                  <p className="text-xs text-slate-400 truncate mt-0.5">{app.location}</p>
                )}
                {(app.salaryRangeMin || app.salaryRangeMax) && (
                  <p className="text-xs text-slate-400 truncate mt-0.5">
                    {app.salaryRangeMin && app.salaryRangeMax
                      ? `$${Number(app.salaryRangeMin).toLocaleString()} – $${Number(app.salaryRangeMax).toLocaleString()}`
                      : app.salaryRangeMin
                        ? `From $${Number(app.salaryRangeMin).toLocaleString()}`
                        : `Up to $${Number(app.salaryRangeMax).toLocaleString()}`
                    }
                  </p>
                )}
                <div className="mb-3" />
                <select
                  value={app.status}
                  onChange={(e) => onStatusChange(app.id, e.target.value, e.target.value === 'applied' ? new Date().toISOString() : null)}
                  className="w-full text-xs px-2 py-1.5 border border-slate-200 rounded-lg focus:outline-none cursor-pointer capitalize"
                >
                  <option value="draft">Draft</option>
                  <option value="applied">Applied</option>
                  <option value="interview">Interview</option>
                  <option value="offer">Offer</option>
                  <option value="rejected">Rejected</option>
                </select>
              </>
            )}
          </div>

          {/* Journey Timeline */}
          <div className="p-3 border-b border-slate-200">
            <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wide mb-2">Journey</p>
            {app.statusHistory && app.statusHistory.length > 0 ? (
              <div className="space-y-2">
                {app.statusHistory.map((entry, idx) => {
                  const statusColorMap = {
                    draft: 'bg-amber-400',
                    applied: 'bg-secondary',
                    interview: 'bg-primary',
                    interviewing: 'bg-primary',
                    offer: 'bg-green-500',
                    rejected: 'bg-slate-400',
                    accepted: 'bg-green-600',
                  };
                  const dotColor = statusColorMap[entry.toStatus] || 'bg-slate-300';
                  const isLast = idx === app.statusHistory.length - 1;
                  const date = entry.changedAt
                    ? new Date(entry.changedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                    : null;
                  const statusLabel = {
                    draft: 'Draft',
                    applied: 'Applied',
                    interview: 'Interview',
                    interviewing: 'Interview',
                    offer: 'Offer',
                    rejected: 'Rejected',
                    accepted: 'Accepted',
                  }[entry.toStatus] || entry.toStatus;

                  return (
                    <div key={idx} className="flex items-start gap-2">
                      <div className="flex flex-col items-center mt-0.5">
                        <div className={`w-3 h-3 rounded-full flex-shrink-0 ${dotColor}`} />
                        {!isLast && <div className="w-px h-3 bg-slate-200 mt-0.5" />}
                      </div>
                      <div className="flex items-baseline gap-1.5 min-w-0">
                        <span className={`text-xs font-medium ${isLast ? 'text-slate-800' : 'text-slate-500'}`}>
                          {statusLabel}
                        </span>
                        {date && (
                          <span className="text-[10px] text-slate-400 flex-shrink-0">{date}</span>
                        )}
                        {entry.source === 'system' && (
                          <span className="text-[9px] text-slate-300 flex-shrink-0">auto</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              // Fallback for older applications with no history
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500 flex-shrink-0" />
                  <span className="text-xs text-slate-600">Resume tailored</span>
                </div>
                {['applied', 'interview', 'interviewing', 'offer'].includes(app.status) && (
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-secondary flex-shrink-0" />
                    <span className="text-xs text-slate-600">Applied</span>
                  </div>
                )}
                {['interview', 'interviewing', 'offer'].includes(app.status) && (
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-primary flex-shrink-0" />
                    <span className="text-xs text-slate-600">Interview</span>
                  </div>
                )}
                {app.status === 'offer' && (
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500 flex-shrink-0" />
                    <span className="text-xs text-slate-600">Offer</span>
                  </div>
                )}
              </div>
            )}
            {/* Contextual prompt */}
            {app.status === 'draft' && (
              <button
                onClick={() => onStatusChange(app.id, 'applied', new Date().toISOString())}
                className="mt-3 w-full text-xs text-primary hover:text-primary/80 font-medium cursor-pointer text-left"
              >
                Mark as applied →
              </button>
            )}
            {app.status === 'applied' && (
              <div className="mt-3 space-y-1.5">
                <button
                  onClick={() => onStatusChange(app.id, 'interview')}
                  className="w-full text-xs text-primary hover:text-primary/80 font-medium cursor-pointer text-left"
                >
                  Got an interview? Update status →
                </button>
                <button
                  onClick={() => setActiveSection('notes')}
                  className="w-full text-xs text-slate-500 hover:text-slate-700 font-medium cursor-pointer text-left"
                >
                  Add notes →
                </button>
              </div>
            )}
            {['interview', 'interviewing'].includes(app.status) && (
              <div className="mt-3 space-y-1.5">
                {!app.interviewPrep && (
                  <button
                    onClick={() => setActiveSection('prep')}
                    className="w-full text-xs text-primary hover:text-primary/80 font-medium cursor-pointer text-left"
                  >
                    Generate interview prep →
                  </button>
                )}
                <button
                  onClick={() => setActiveSection('notes')}
                  className="w-full text-xs text-slate-500 hover:text-slate-700 font-medium cursor-pointer text-left"
                >
                  Add notes about this interview →
                </button>
              </div>
            )}
            {app.status === 'offer' && (
              <div className="mt-3 space-y-1.5">
                <button
                  onClick={() => setActiveSection('notes')}
                  className="w-full text-xs text-green-600 hover:text-green-700 font-medium cursor-pointer text-left"
                >
                  Log offer details (salary, terms) →
                </button>
              </div>
            )}
            {app.status === 'rejected' && (
              <div className="mt-3 space-y-1.5">
                {(app.statusHistory || []).some(h => ['interview', 'interviewing', 'offer'].includes(h.toStatus)) && (
                  <button
                    onClick={() => setActiveSection('notes')}
                    className="w-full text-xs text-slate-500 hover:text-slate-700 font-medium cursor-pointer text-left"
                  >
                    Add notes on what happened →
                  </button>
                )}
                <button
                  onClick={onStartNewApplication}
                  className="w-full text-xs text-primary hover:text-primary/80 font-medium cursor-pointer text-left"
                >
                  Keep going — tailor another →
                </button>
              </div>
            )}
          </div>

          <nav className="flex-1 p-2 space-y-0.5">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveSection(item.id)}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm cursor-pointer ${
                  activeSection === item.id
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600 hover:bg-white/50'
                }`}
              >
                <item.icon size={16} className="text-slate-400" />
                {item.label}
              </button>
            ))}
          </nav>

          <div className="p-2">
            <button
              onClick={() => onArchive(app.id, !app.archived)}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-slate-500 hover:bg-slate-100 rounded-lg cursor-pointer"
            >
              {app.archived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
              {app.archived ? 'Unarchive' : 'Archive'}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header */}
          <div className="hidden sm:flex items-center justify-between px-6 py-4 border-b border-slate-100">
            <h3 className="font-medium text-slate-900">{navItems.find(n => n.id === activeSection)?.label}</h3>
            <div className="flex items-center gap-2">
              {activeSection === 'resume' && (
                <>
                  {onDownloadDocx && (
                    <button onClick={() => onDownloadDocx(app.resume, `${app.company || 'Tailored'}_Resume.docx`)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 cursor-pointer">
                      <Download size={14} />
                      .docx
                    </button>
                  )}
                  <button onClick={handleDownloadTxt} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg cursor-pointer">
                    <Download size={14} />
                    .txt
                  </button>
                  <button onClick={() => handleCopy(app.resume || '', 'resume')} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg cursor-pointer">
                    {copied === 'resume' ? <Check size={14} /> : <Copy size={14} />}
                    {copied === 'resume' ? 'Copied' : 'Copy'}
                  </button>
                </>
              )}
              {activeSection === 'notes' && (
                <button onClick={saveNotes} disabled={savingNotes} className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 cursor-pointer disabled:opacity-50">
                  {savingNotes ? <Loader2 size={14} className="animate-spin" /> : 'Save'}
                </button>
              )}
              <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 cursor-pointer">
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6" style={{ WebkitOverflowScrolling: 'touch' }}>
            {/* Resume */}
            {activeSection === 'resume' && (
              <div className="space-y-4">
                {app.changesSummary && (
                  <button
                    onClick={() => setShowChanges(!showChanges)}
                    className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 cursor-pointer"
                  >
                    <Sparkles size={14} />
                    What was tailored
                    <ChevronDown size={14} className={`transition-transform ${showChanges ? 'rotate-180' : ''}`} />
                  </button>
                )}
                {showChanges && app.changesSummary && (
                  <div className="p-3 bg-slate-50 rounded-lg text-sm text-slate-600">
                    {app.changesSummary}
                  </div>
                )}
                <div className="bg-slate-50 rounded-lg p-5">
                  <pre className="whitespace-pre-wrap font-sans text-sm text-slate-700 leading-relaxed">
                    {app.resume || 'No resume content'}
                  </pre>
                </div>
              </div>
            )}

            {/* Job Description */}
            {activeSection === 'jobdesc' && (
              <div>
                {isEditingJobDesc ? (
                  <div className="space-y-3">
                    <textarea
                      value={editedJobDescription}
                      onChange={(e) => setEditedJobDescription(e.target.value)}
                      rows={20}
                      className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary resize-y font-sans leading-relaxed"
                      placeholder="Paste the job description here..."
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={saveJobDescription}
                        disabled={savingJobDesc}
                        className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-dark cursor-pointer disabled:opacity-50"
                      >
                        {savingJobDesc ? 'Saving…' : 'Save'}
                      </button>
                      <button
                        onClick={() => setIsEditingJobDesc(false)}
                        className="px-4 py-2 text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 cursor-pointer"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-end gap-3 mb-4">
                      {app.jobDescription && (
                        <button onClick={() => handleCopy(app.jobDescription, 'jd')} className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 cursor-pointer">
                          {copied === 'jd' ? <Check size={14} /> : <Copy size={14} />}
                          {copied === 'jd' ? 'Copied' : 'Copy'}
                        </button>
                      )}
                      <button
                        onClick={() => { setEditedJobDescription(app.jobDescription || ''); setIsEditingJobDesc(true); }}
                        className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 cursor-pointer"
                      >
                        <Edit2 size={14} />
                        Edit
                      </button>
                    </div>
                    {app.jobDescription ? (
                      <div className="bg-slate-50 rounded-lg p-5">
                        <pre className="whitespace-pre-wrap font-sans text-sm text-slate-600 leading-relaxed">
                          {app.jobDescription}
                        </pre>
                      </div>
                    ) : (
                      <div className="text-center py-12 text-slate-400">
                        <p className="text-sm mb-3">No job description saved</p>
                        <button
                          onClick={() => { setEditedJobDescription(''); setIsEditingJobDesc(true); }}
                          className="text-sm text-primary hover:underline cursor-pointer"
                        >
                          Add one →
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Q&A */}
            {activeSection === 'questions' && (
              <div className="space-y-4">
                <p className="text-sm text-slate-500">
                  Generate responses to application questions using your tailored resume.
                </p>

                {questions.map((q, i) => (
                  <div key={i} className="bg-slate-50 rounded-lg p-4 space-y-3">
                    <textarea
                      value={q.question}
                      onChange={(e) => updateQuestion(i, 'question', e.target.value)}
                      placeholder="Paste a question..."
                      className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm resize-none focus:outline-none focus:border-slate-400"
                      rows={2}
                    />
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <span>Limit:</span>
                        <input
                          type="number"
                          value={q.charLimit}
                          onChange={(e) => updateQuestion(i, 'charLimit', parseInt(e.target.value) || 500)}
                          className="w-16 px-2 py-1 border border-slate-200 rounded text-xs focus:outline-none"
                        />
                      </div>
                      <button
                        onClick={() => generateResponse(i)}
                        disabled={isGenerating || !q.question.trim()}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 cursor-pointer disabled:opacity-50"
                      >
                        {isGenerating && generatingIndex === i ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                        Generate
                      </button>
                    </div>
                    {q.response && (
                      <div className="pt-3 border-t border-slate-200 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-slate-500">{q.response.length}/{q.charLimit}</span>
                          <button
                            onClick={() => handleCopy(q.response, `q${i}`)}
                            className="flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900 cursor-pointer"
                          >
                            {copied === `q${i}` ? <Check size={12} /> : <Copy size={12} />}
                            {copied === `q${i}` ? 'Copied' : 'Copy'}
                          </button>
                        </div>
                        <textarea
                          value={q.response}
                          onChange={(e) => updateQuestion(i, 'response', e.target.value)}
                          className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-400"
                          rows={4}
                        />
                      </div>
                    )}
                  </div>
                ))}
                <button
                  onClick={() => setQuestions([...questions, { question: '', response: '', charLimit: 500, expanded: false }])}
                  className="w-full py-2 border border-dashed border-slate-300 rounded-lg text-sm text-slate-500 hover:border-slate-400 hover:text-slate-600 cursor-pointer"
                >
                  + Add question
                </button>
              </div>
            )}

            {/* Rounds */}
            {activeSection === 'rounds' && (
              <div className="space-y-4">
                {loadingRounds ? (
                  <div className="text-center py-8 text-slate-400">Loading...</div>
                ) : (
                  <>
                    {interviewRounds.map((round) => (
                      editingRound === round.id ? (
                        <RoundForm key={round.id} round={round} onSave={(data) => updateInterviewRound(round.id, data)} onCancel={() => setEditingRound(null)} />
                      ) : (
                        <div key={round.id} className="bg-slate-50 rounded-lg p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <span className="font-medium text-slate-900 text-sm">
                                {ROUND_TYPES.find(t => t.value === round.roundType)?.label}
                              </span>
                              <span className="ml-2 text-xs text-slate-500">{round.status}</span>
                            </div>
                            <div className="flex gap-1">
                              <button onClick={() => setEditingRound(round.id)} className="p-1 text-slate-400 hover:text-slate-600 cursor-pointer">
                                <Edit2 size={14} />
                              </button>
                              <button onClick={() => deleteInterviewRound(round.id)} className="p-1 text-slate-400 hover:text-red-500 cursor-pointer">
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>
                          {round.scheduledDate && (
                            <p className="text-xs text-slate-500 mb-1">
                              {new Date(round.scheduledDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                            </p>
                          )}
                          {round.interviewers && <p className="text-xs text-slate-500 mb-1">{round.interviewers}</p>}
                          {round.notes && <p className="text-sm text-slate-600 mt-2">{round.notes}</p>}
                        </div>
                      )
                    ))}

                    {newRound ? (
                      <RoundForm round={newRound} onSave={addInterviewRound} onCancel={() => setNewRound(null)} />
                    ) : (
                      <button
                        onClick={() => setNewRound(createEmptyRound())}
                        className="w-full py-2 border border-dashed border-slate-300 rounded-lg text-sm text-slate-500 hover:border-slate-400 hover:text-slate-600 cursor-pointer flex items-center justify-center gap-2"
                      >
                        <Plus size={14} />
                        Add round
                      </button>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Notes */}
            {activeSection === 'notes' && (
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add notes..."
                className="w-full h-[400px] px-4 py-3 bg-slate-50 border-0 rounded-lg text-sm resize-none focus:outline-none"
              />
            )}

            {/* Prep */}
            {activeSection === 'prep' && (
              <div>
                {!prepGuide ? (
                  <div className="text-center py-16">
                    <p className="text-slate-500 mb-4">Generate a personalized interview prep guide</p>
                    <button
                      onClick={getInterviewPrep}
                      disabled={loadingPrep}
                      className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-xl hover:bg-primary/90 cursor-pointer disabled:opacity-50 mx-auto"
                    >
                      {loadingPrep ? <Loader2 size={16} className="animate-spin" /> : <BookOpen size={16} />}
                      Generate Guide
                    </button>
                    <p className="text-xs text-slate-400 mt-2">Uses 1 credit</p>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-end mb-4">
                      <button onClick={() => handleCopy(prepGuide, 'prep')} className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 cursor-pointer">
                        {copied === 'prep' ? <Check size={14} /> : <Copy size={14} />}
                        {copied === 'prep' ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-5">
                      <pre className="whitespace-pre-wrap font-sans text-sm text-slate-700 leading-relaxed">{prepGuide}</pre>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ============================================================================
// Main LibraryView
// ============================================================================
function LibraryView({
  resumeLibrary,
  onStatusChange,
  onUpdateApplication,
  onStartNewApplication,
  onCreateManualApplication,
  onDownloadDocx,
  onArchive,
  authToken,
  initialFilter = 'all'
}) {
  const [filter, setFilter] = useState(initialFilter);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('libraryViewMode') || 'list');
  const [selectedApp, setSelectedApp] = useState(null);
  const [showManualModal, setShowManualModal] = useState(false);
  const [manualCompany, setManualCompany] = useState('');
  const [manualRole, setManualRole] = useState('');
  const [manualSalaryRangeMin, setManualSalaryRangeMin] = useState('');
  const [manualSalaryRangeMax, setManualSalaryRangeMax] = useState('');
  const [manualLocation, setManualLocation] = useState('');
  const [manualStatus, setManualStatus] = useState('applied');
  const [manualJobDescription, setManualJobDescription] = useState('');
  const [manualNotes, setManualNotes] = useState('');
  const [manualRounds, setManualRounds] = useState([]);
  const [manualError, setManualError] = useState('');
  const [savingManual, setSavingManual] = useState(false);

  const resetManualForm = () => {
    setManualCompany('');
    setManualRole('');
    setManualSalaryRangeMin('');
    setManualSalaryRangeMax('');
    setManualLocation('');
    setManualStatus('applied');
    setManualJobDescription('');
    setManualNotes('');
    setManualRounds([]);
    setManualError('');
    setSavingManual(false);
  };

  const openManualModal = () => {
    resetManualForm();
    setShowManualModal(true);
  };

  const closeManualModal = () => {
    if (savingManual) return;
    setShowManualModal(false);
    setManualError('');
  };

  const updateManualRound = (index, field, value) => {
    setManualRounds(prev => prev.map((round, idx) => (
      idx === index ? { ...round, [field]: value } : round
    )));
  };

  const addManualRound = () => {
    setManualRounds(prev => [...prev, createEmptyRound()]);
  };

  const removeManualRound = (index) => {
    setManualRounds(prev => prev.filter((_, idx) => idx !== index));
  };

  const saveManualApplication = async () => {
    const company = manualCompany.trim();
    if (!company) {
      setManualError('Company name is required.');
      return;
    }

    if (!onCreateManualApplication) {
      setManualError('Manual application creation is not available right now.');
      return;
    }

    setSavingManual(true);
    setManualError('');

    try {
      await onCreateManualApplication({
        company,
        role: manualRole.trim(),
        salaryRangeMin: manualSalaryRangeMin ? Number(manualSalaryRangeMin) : null,
        salaryRangeMax: manualSalaryRangeMax ? Number(manualSalaryRangeMax) : null,
        location: manualLocation.trim() || null,
        status: manualStatus,
        jobDescription: manualJobDescription.trim(),
        notes: manualNotes.trim(),
        rounds: manualRounds.map(normalizeRound)
      });
      setFilter('all');
      setSearch('');
      setShowManualModal(false);
      resetManualForm();
    } catch (error) {
      setManualError(error.message || 'Failed to save manual application.');
    } finally {
      setSavingManual(false);
    }
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    localStorage.setItem('libraryViewMode', mode);
  };

  useEffect(() => {
    if (initialFilter && initialFilter !== 'all') {
      setFilter(initialFilter);
    }
  }, [initialFilter]);

  const filteredApps = useMemo(() => {
    let result = [...resumeLibrary];

    if (filter === 'archived') {
      result = result.filter(a => a.archived === true);
    } else {
      result = result.filter(a => !a.archived);
      if (filter !== 'all') {
        // Handle both 'interview' and 'interviewing' statuses
        if (filter === 'interview') {
          result = result.filter(a => a.status === 'interview' || a.status === 'interviewing');
        } else {
          result = result.filter(a => a.status === filter);
        }
      }
    }

    if (search) {
      const q = search.toLowerCase();
      result = result.filter(a =>
        a.company?.toLowerCase().includes(q) ||
        a.role?.toLowerCase().includes(q) ||
        a.notes?.toLowerCase().includes(q)
      );
    }
    result.sort((a, b) => {
      if (sortBy === 'date') return new Date(b.timestamp) - new Date(a.timestamp);
      if (sortBy === 'company') return (a.company || '').localeCompare(b.company || '');
      if (sortBy === 'ats') return (b.qualityScore || 0) - (a.qualityScore || 0);
      return 0;
    });
    return result;
  }, [resumeLibrary, filter, search, sortBy]);

  const counts = useMemo(() => {
    const nonArchived = resumeLibrary.filter(a => !a.archived);
    const archived = resumeLibrary.filter(a => a.archived === true);
    const c = { all: nonArchived.length, draft: 0, applied: 0, interview: 0, offer: 0, rejected: 0, archived: archived.length };
    nonArchived.forEach(a => {
      // Normalize 'interviewing' to 'interview' for consistent counting
      const status = a.status === 'interviewing' ? 'interview' : a.status;
      if (c.hasOwnProperty(status)) c[status]++;
    });
    return c;
  }, [resumeLibrary]);

  return (
    <div className="max-w-4xl mx-auto space-y-4 sm:space-y-6">
      {/* Header with stats */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-5">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="min-w-0">
            <h1 className="font-heading text-xl sm:text-2xl font-bold text-slate-900 truncate">Your Applications</h1>
            <p className="text-slate-500 text-xs sm:text-sm mt-0.5 truncate">
              {counts.all === 0 ? "Let's get started" :
               counts.interview > 0 ? `${counts.interview} interview${counts.interview > 1 ? 's' : ''} in progress` :
               counts.applied > 0 ? `${counts.applied} waiting to hear back` :
               `${counts.all} application${counts.all > 1 ? 's' : ''} tracked`}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={openManualModal}
              className="inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-white text-slate-700 text-sm font-medium rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors cursor-pointer"
              aria-label="Add manual application"
            >
              <Plus size={16} aria-hidden="true" />
              <span className="hidden sm:inline">Add manually</span>
            </button>
            <button
              onClick={onStartNewApplication}
              className="inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-primary text-white text-sm font-medium rounded-xl hover:bg-dark transition-colors cursor-pointer"
              aria-label="Tailor new application with AI"
            >
              <Plus size={16} aria-hidden="true" />
              <span className="hidden sm:inline">Tailor with AI</span>
            </button>
          </div>
        </div>

        {/* Quick stats */}
        {counts.all > 0 && (
          <div className="flex flex-wrap gap-x-4 gap-y-2 pt-4 border-t border-slate-100">
            {counts.interview > 0 && (
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-primary"></span>
                <span className="text-xs sm:text-sm text-slate-600"><strong className="text-slate-900">{counts.interview}</strong> interviewing</span>
              </div>
            )}
            {counts.offer > 0 && (
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-green-500"></span>
                <span className="text-xs sm:text-sm text-slate-600"><strong className="text-slate-900">{counts.offer}</strong> offer{counts.offer > 1 ? 's' : ''}</span>
              </div>
            )}
            {counts.applied > 0 && (
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-secondary"></span>
                <span className="text-xs sm:text-sm text-slate-600"><strong className="text-slate-900">{counts.applied}</strong> applied</span>
              </div>
            )}
            {counts.draft > 0 && (
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-amber-400"></span>
                <span className="text-xs sm:text-sm text-slate-600"><strong className="text-slate-900">{counts.draft}</strong> draft{counts.draft > 1 ? 's' : ''}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Search & Sort */}
      <div className="flex gap-2 sm:gap-3">
        <div className="flex-1 relative min-w-0">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-400"
          />
        </div>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="px-2 sm:px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 focus:outline-none cursor-pointer flex-shrink-0"
        >
          <option value="date">Newest</option>
          <option value="company">A-Z</option>
          <option value="ats">Score</option>
        </select>
        <div className="hidden sm:flex border border-slate-200 rounded-lg">
          <button
            onClick={() => handleViewModeChange('list')}
            className={`p-2 cursor-pointer ${viewMode === 'list' ? 'bg-slate-100' : 'hover:bg-slate-50'}`}
          >
            <List size={16} className="text-slate-600" />
          </button>
          <button
            onClick={() => handleViewModeChange('cards')}
            className={`p-2 cursor-pointer ${viewMode === 'cards' ? 'bg-slate-100' : 'hover:bg-slate-50'}`}
          >
            <LayoutGrid size={16} className="text-slate-600" />
          </button>
        </div>
      </div>

      {/* Filters */}
      <StatusFilters counts={counts} active={filter} onChange={setFilter} />

      {/* List */}
      {filteredApps.length === 0 ? (
        <div className="text-center py-16 px-4">
          {search || filter !== 'all' ? (
            <>
              <p className="text-slate-500 mb-2">No applications match your search</p>
              <button
                onClick={() => { setSearch(''); setFilter('all'); }}
                className="text-primary hover:text-primary/80 font-medium cursor-pointer"
              >
                Clear filters
              </button>
            </>
          ) : (
            <div className="max-w-sm mx-auto">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-primary/10 flex items-center justify-center">
                <Briefcase size={28} className="text-primary" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Ready to start applying?</h3>
              <p className="text-slate-500 mb-6">
                Every great job starts with a great application. Add a note or tailor a resume to get started.
              </p>
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={openManualModal}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-slate-700 font-medium rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors cursor-pointer"
                  aria-label="Add first manual application"
                >
                  <Plus size={18} aria-hidden="true" />
                  Add manually
                </button>
                <button
                  onClick={onStartNewApplication}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-white font-medium rounded-xl hover:bg-dark transition-colors cursor-pointer"
                  aria-label="Create your first AI tailored application"
                >
                  <Plus size={18} aria-hidden="true" />
                  Tailor with AI
                </button>
              </div>
            </div>
          )}
        </div>
      ) : viewMode === 'cards' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredApps.map((app) => (
            <ApplicationCard key={app.id} app={app} onView={setSelectedApp} onArchive={onArchive} />
          ))}
        </div>
      ) : (
        <div className="space-y-1">
          {filteredApps.map((app) => (
            <ApplicationRow key={app.id} app={app} onView={setSelectedApp} onStatusChange={onStatusChange} onArchive={onArchive} />
          ))}
        </div>
      )}

      {showManualModal && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={closeManualModal} />
          <div className="relative bg-white rounded-xl border border-slate-200 shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <div>
                <h2 className="font-heading text-xl font-bold text-slate-900">Add Manual Application</h2>
                <p className="text-sm text-slate-500">Free tracking with notes and interview rounds.</p>
              </div>
              <button
                onClick={closeManualModal}
                className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 cursor-pointer"
                aria-label="Close manual application modal"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-5 space-y-5">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Company</label>
                  <input
                    type="text"
                    value={manualCompany}
                    onChange={(e) => {
                      setManualCompany(e.target.value);
                      if (manualError) setManualError('');
                    }}
                    placeholder="e.g. Fanatics"
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Role</label>
                  <input
                    type="text"
                    value={manualRole}
                    onChange={(e) => setManualRole(e.target.value)}
                    placeholder="e.g. Principal Software Engineer"
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Location</label>
                <input
                  type="text"
                  value={manualLocation}
                  onChange={(e) => setManualLocation(e.target.value)}
                  placeholder="e.g. San Francisco, CA or Remote"
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Salary Range</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={manualSalaryRangeMin}
                    onChange={(e) => setManualSalaryRangeMin(e.target.value)}
                    placeholder="Min"
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                  />
                  <span className="text-slate-400 flex-shrink-0">–</span>
                  <input
                    type="number"
                    value={manualSalaryRangeMax}
                    onChange={(e) => setManualSalaryRangeMax(e.target.value)}
                    placeholder="Max"
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Status</label>
                <select
                  value={manualStatus}
                  onChange={(e) => setManualStatus(e.target.value)}
                  className="w-full sm:w-56 px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary cursor-pointer"
                >
                  <option value="draft">Draft</option>
                  <option value="applied">Applied</option>
                  <option value="interview">Interview</option>
                  <option value="offer">Offer</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Job Description</label>
                <textarea
                  value={manualJobDescription}
                  onChange={(e) => setManualJobDescription(e.target.value)}
                  rows={5}
                  placeholder="Paste the job description here."
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm resize-y focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Notes</label>
                <textarea
                  value={manualNotes}
                  onChange={(e) => setManualNotes(e.target.value)}
                  rows={4}
                  placeholder="Add notes, links, follow-up reminders, or context."
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm resize-y focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                />
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-slate-900">Interview Rounds</h3>
                    <p className="text-xs text-slate-500">Optional, add all rounds you want to track.</p>
                  </div>
                  <button
                    onClick={addManualRound}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 cursor-pointer"
                  >
                    <Plus size={14} />
                    Add round
                  </button>
                </div>

                {manualRounds.length === 0 ? (
                  <div className="text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-3">
                    No rounds yet.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {manualRounds.map((round, index) => (
                      <div key={`manual-round-${index}`} className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-3">
                        <div className="grid sm:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs text-slate-500 mb-1">Type</label>
                            <select
                              value={round.roundType}
                              onChange={(e) => updateManualRound(index, 'roundType', e.target.value)}
                              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary cursor-pointer"
                            >
                              {ROUND_TYPES.map(type => (
                                <option key={type.value} value={type.value}>{type.label}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs text-slate-500 mb-1">Date</label>
                            <input
                              type="date"
                              value={round.scheduledDate ? round.scheduledDate.slice(0, 10) : ''}
                              onChange={(e) => {
                                updateManualRound(index, 'scheduledDate', e.target.value);
                                updateManualRound(index, 'status', deriveRoundStatus(e.target.value));
                              }}
                              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs text-slate-500 mb-1">Interviewers</label>
                          <input
                            type="text"
                            value={round.interviewers}
                            onChange={(e) => updateManualRound(index, 'interviewers', e.target.value)}
                            placeholder="Names..."
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-slate-500 mb-1">Round notes</label>
                          <textarea
                            value={round.notes}
                            onChange={(e) => updateManualRound(index, 'notes', e.target.value)}
                            rows={4}
                            placeholder="How did it go? Key topics, impressions..."
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-y focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                          />
                        </div>
                        <div className="flex justify-end">
                          <button
                            onClick={() => removeManualRound(index)}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg cursor-pointer"
                          >
                            <Trash2 size={12} />
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {manualError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-red-700 text-sm">{manualError}</p>
                </div>
              )}
            </div>

            <div className="px-5 py-4 border-t border-slate-100 flex items-center justify-end gap-2">
              <button
                onClick={closeManualModal}
                disabled={savingManual}
                className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg cursor-pointer disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={saveManualApplication}
                disabled={savingManual}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-primary text-white rounded-lg hover:bg-dark cursor-pointer disabled:opacity-60"
              >
                {savingManual ? <Loader2 size={14} className="animate-spin" /> : null}
                Save manual application
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Detail Panel */}
      <ApplicationDetailPanel
        app={selectedApp}
        isOpen={!!selectedApp}
        onClose={() => setSelectedApp(null)}
        onDownloadDocx={onDownloadDocx}
        authToken={authToken}
        onUpdateApplication={(id, updates) => {
          onUpdateApplication(id, updates);
          // Update selectedApp so UI reflects changes immediately
          setSelectedApp(prev => prev ? { ...prev, ...updates } : null);
        }}
        onStatusChange={(id, status, appliedDate) => {
          onStatusChange(id, status, appliedDate);
          setSelectedApp(prev => prev ? { ...prev, status } : null);
        }}
        onArchive={(id, archived) => {
          onArchive(id, archived);
          setSelectedApp(prev => prev ? { ...prev, archived } : null);
        }}
        onStartNewApplication={onStartNewApplication}
      />
    </div>
  );
}

export default LibraryView;
